/**
 * Shared, per-device chat-translation preference (customer portal).
 * `on` = auto-translate incoming messages; `lang` = the language to translate INTO.
 * Components subscribe so a change in one chat updates every open surface.
 */
const ON_KEY = 'cu_at_on';
const LANG_KEY = 'cu_translate_lang'; // shared with the old picker
const EVENT = 'cu_translate_pref_change';

export const TRANSLATE_LANGS: Array<{ code: string; label: string }> = [
  { code: 'English', label: 'English' },
  { code: 'Arabic', label: 'العربية' },
  { code: 'Hindi', label: 'हिन्दी' },
  { code: 'Urdu', label: 'اردو' },
  { code: 'Malayalam', label: 'മലയാളം' },
  { code: 'Tamil', label: 'தமிழ்' },
  { code: 'Telugu', label: 'తెలుగు' },
  { code: 'Bengali', label: 'বাংলা' },
  { code: 'Tagalog', label: 'Tagalog' },
  { code: 'Nepali', label: 'नेपाली' },
  { code: 'French', label: 'Français' },
  { code: 'Spanish', label: 'Español' },
  { code: 'Russian', label: 'Русский' },
  { code: 'Chinese', label: '中文' },
];

export function deviceLang(): string {
  const nav = (typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en').slice(0, 2).toLowerCase();
  const map: Record<string, string> = { ar: 'Arabic', hi: 'Hindi', ur: 'Urdu', ml: 'Malayalam', ta: 'Tamil', te: 'Telugu', bn: 'Bengali', ne: 'Nepali', fr: 'French', es: 'Spanish', ru: 'Russian', zh: 'Chinese', tl: 'Tagalog' };
  return map[nav] || 'English';
}

export interface TranslatePref { on: boolean; lang: string }

// The device-wide default (used for any chat that hasn't been individually set).
function globalDefault(): TranslatePref {
  let on = true, lang = deviceLang();
  try {
    const o = localStorage.getItem(ON_KEY); if (o != null) on = o === '1';
    const l = localStorage.getItem(LANG_KEY); if (l) lang = l;
  } catch { /* ignore */ }
  return { on, lang };
}

/**
 * Preference for one conversation (table/booking). Each chat remembers its OWN "translate to" so a
 * staff member handling several tables can set each independently; a chat that's never been touched
 * uses the device default. Pass no id to read/write the device default itself.
 */
export function getPref(convId?: string): TranslatePref {
  if (convId) {
    try {
      const raw = localStorage.getItem(`${ON_KEY}_conv_${convId}`);
      if (raw) { const p = JSON.parse(raw); return { on: p.on !== false, lang: p.lang || deviceLang() }; }
    } catch { /* ignore */ }
  }
  return globalDefault();
}

export function setPref(convId: string | undefined, p: Partial<TranslatePref>): void {
  try {
    if (convId) {
      const cur = getPref(convId);
      const next = { on: p.on != null ? p.on : cur.on, lang: p.lang || cur.lang };
      localStorage.setItem(`${ON_KEY}_conv_${convId}`, JSON.stringify(next));
    } else {
      if (p.on != null) localStorage.setItem(ON_KEY, p.on ? '1' : '0');
      if (p.lang) localStorage.setItem(LANG_KEY, p.lang);
    }
  } catch { /* ignore */ }
  try { window.dispatchEvent(new Event(EVENT)); } catch { /* ignore */ }
}

export function subscribePref(cb: () => void): () => void {
  const h = () => cb();
  window.addEventListener(EVENT, h);
  window.addEventListener('storage', h);
  return () => { window.removeEventListener(EVENT, h); window.removeEventListener('storage', h); };
}

// ── Sender's INPUT language (source hint) ──────────────────────────────────────
// The language the sender is typing in, per conversation. '' = auto-detect (no hint sent).
// Passed with the message so the recipient's auto-translate is source-accurate.
const INPUT_KEY = 'cu_input_lang';

export function getInputLang(convId?: string): string {
  try {
    if (convId) { const v = localStorage.getItem(`${INPUT_KEY}_conv_${convId}`); if (v != null) return v; }
    return localStorage.getItem(INPUT_KEY) || '';
  } catch { return ''; }
}

export function setInputLang(convId: string | undefined, lang: string): void {
  try {
    if (convId) localStorage.setItem(`${INPUT_KEY}_conv_${convId}`, lang);
    else localStorage.setItem(INPUT_KEY, lang);
  } catch { /* ignore */ }
  try { window.dispatchEvent(new Event(EVENT)); } catch { /* ignore */ }
}
